import { isSettled } from '@/stores/deals.store';
import { SERVER_STEP_STATE, type Action, type Deal, type DealStep, type StatusCard, type StepStateEntry } from '@/types/deal';
import type { SocialDealStage } from '@/types/socialDeal';

/** True while the server marks `step` as the user's to do (its button shows). */
export function stepIsOpen(action: Action | undefined, step: DealStep): boolean {
  return action?.step_states?.[step.key]?.state === SERVER_STEP_STATE.Active;
}

/** The deal's playbook step (the one whose button opens the playbook), if it has one. */
export function playbookStep(deal: Deal): DealStep | undefined {
  return deal.steps_config.steps.find((s) => s.ctas?.some((c) => c.action === 'voltr_brief'));
}

/** True once the server has opened the playbook: its step is theirs to do, or done. */
export function playbookReadable(deal: Deal, action?: Action): boolean {
  const step = playbookStep(deal);
  const state = step && action?.step_states?.[step.key]?.state;
  return !!state && state !== SERVER_STEP_STATE.Locked;
}

/** The server's stage for the Create card and its filters; no Action yet reads as new. */
export function socialStage(action?: Action): SocialDealStage {
  return action?.voltr?.stage ?? 'new';
}

/**
 * The server's card for a deal that can no longer change (approved, declined,
 * expired): its title and words, for the deal page's header and the Create
 * card. Undefined while the deal is under way, when the server's card speaks
 * to the steps ("Hang tight! We're checking Step 1") and the header has its own.
 */
export function settledCard(action?: Action): StatusCard | undefined {
  return isSettled(action) ? (action?.status_card ?? undefined) : undefined;
}

/** The package left and hasn't arrived: the tracker's last stage isn't reached yet. */
export function inTransit(entry?: StepStateEntry): boolean {
  const stages = entry?.tracker?.stages ?? [];
  return stages.length > 0 && !stages[stages.length - 1]?.reached;
}

/**
 * True once the athlete's package has shipped and until it lands: the server
 * sends its carrier card (`tracking`) from Shipped on (Figma "Create Deal
 * Detail 5": the header's Track package).
 */
export function packageInTransit(action?: Action): boolean {
  return Object.values(action?.step_states ?? {}).some((entry) => !!entry?.tracker?.tracking && inTransit(entry));
}
